/**
 * The small things done to a tab that is already open: how many are at the
 * table, which table it is, and what has happened to it so far.
 *
 * All three had working endpoints and no way to reach them from the till. A
 * party of four that became five kept "4 guests" on the bill; a table moved to
 * the window was a manager's job at the back office; "who took the dessert
 * off?" was a question for SQL.
 */

import { useState } from 'react';
import {
  describeLedgerEntry,
  displayLocale,
  useEntitlements,
  useFloor,
  useLogins,
  useMe,
  useOrderLedger,
  usePermission,
  useSetGuestCount,
  useTransferTable,
  type Order,
} from '@restaurant/api-client';
import {
  ActionError,
  Button,
  Empty,
  ErrorNotice,
  Loading,
  Sheet,
  UpgradePrompt,
} from '@restaurant/ui';

export function GuestsSheet({ order, onDone }: { order: Order; onDone: () => void }) {
  const setGuests = useSetGuestCount();
  const [count, setCount] = useState(order.guest_count);

  return (
    <Sheet
      title={`Guests on #${order.number}`}
      onDismiss={onDone}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={count === order.guest_count}
          loading={setGuests.isPending}
          onClick={() =>
            setGuests.mutate({ orderId: order.id, guestCount: count }, { onSuccess: onDone })
          }
        >
          Save
        </Button>
      }
    >
      <div className="tt__stepper" role="group" aria-label="Guests">
        <button
          type="button"
          className="tt__step"
          disabled={count <= 0}
          onClick={() => setCount((n) => Math.max(0, n - 1))}
          aria-label="One fewer"
        >
          −
        </button>
        <output className="tt__count tabular" aria-live="polite">
          {count}
        </output>
        <button
          type="button"
          className="tt__step"
          disabled={count >= 999}
          onClick={() => setCount((n) => Math.min(999, n + 1))}
          aria-label="One more"
        >
          +
        </button>
      </div>
      <p className="os__sheet-note">
        Covers are what the per-guest figures divide by, so a table that grew is worth saying.
      </p>
      <ActionError error={setGuests.error} action="Changing the guests" title="Not changed" />
    </Sheet>
  );
}

/** A party moving to another table, and every tab on theirs going with them. */
export function MoveTableSheet({
  order,
  locationId,
  onDone,
}: {
  order: Order;
  locationId: string | undefined;
  onDone: () => void;
}) {
  const entitlements = useEntitlements();
  const floor = useFloor(locationId);
  const transfer = useTransferTable();
  const allowed = entitlements.has('floor.table_ops');

  const tables = (floor.data ?? []).flatMap((plan) => plan.tables);
  const here = tables.find((table) => table.id === order.table_id);
  const free = tables.filter((table) => table.can_seat && table.id !== order.table_id);

  return (
    <Sheet
      title={`Move #${order.number}${here ? ` from ${here.label}` : ''}`}
      onDismiss={onDone}
    >
      {!allowed ? (
        <UpgradePrompt
          action="Moving a party to another table"
          currentPlan={entitlements.plan}
          requiredPlan="professional"
        />
      ) : floor.isPending ? (
        <Loading label="Reading the room" />
      ) : floor.isError ? (
        <ErrorNotice message={floor.error.message} />
      ) : free.length === 0 ? (
        <Empty title="No free table" description="Every other table has somebody at it." />
      ) : (
        <>
          <p className="os__sheet-note">
            Everything on {here ? `table ${here.label}` : 'this table'} moves: the party, and
            every tab they have open.
          </p>
          <div className="tt__tables">
            {free.map((table) => (
              <button
                key={table.id}
                type="button"
                className="tt__table"
                disabled={transfer.isPending}
                onClick={() =>
                  order.table_id &&
                  transfer.mutate(
                    { fromTableId: order.table_id, toTableId: table.id },
                    { onSuccess: onDone },
                  )
                }
              >
                <span className="tt__table-label">{table.label}</span>
                <span className="tt__table-seats">{table.seats} seats</span>
              </button>
            ))}
          </div>
        </>
      )}
      <ActionError error={transfer.error} action="Moving the table" title="Not moved" />
    </Sheet>
  );
}

/** What has happened to a tab, oldest first, with who did it where that is known. */
export function HistorySheet({ order, onDone }: { order: Order; onDone: () => void }) {
  const ledger = useOrderLedger(order.id);
  const me = useMe();
  // Names need the logins list, which a waiter's role cannot read. Without it
  // the history still says what happened, and names only "you".
  const mayReadLogins = usePermission('identity.user.read');
  const logins = useLogins({ enabled: mayReadLogins });
  const names = new Map((logins.data ?? []).map((login) => [login.id, login.name]));

  const who = (actorId: string | null): string | null => {
    if (!actorId) return null;
    if (actorId === me.data?.user_id) return 'you';
    return names.get(actorId) ?? null;
  };

  return (
    <Sheet title={`History of #${order.number}`} onDismiss={onDone}>
      {ledger.isPending ? (
        <Loading label="Reading the tab" />
      ) : ledger.isError ? (
        <ErrorNotice message={ledger.error.message} />
      ) : (
        <ol className="tt__history">
          {ledger.data.map((entry) => (
            <li key={entry.seq} className="tt__event">
              <time className="tt__time tabular" dateTime={entry.occurred_at}>
                {new Date(entry.occurred_at).toLocaleTimeString(displayLocale(), {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </time>
              <span className="tt__what">
                {describeLedgerEntry(entry, order.lines)}
                {who(entry.actor_id) && (
                  <span className="tt__who"> · {who(entry.actor_id)}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Sheet>
  );
}
